import React, { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import { BreakpointsType } from '@/shared/types/typings';
import { Button, Col, Form, Input, Modal, Row, Select } from 'antd';
import { EventSource } from '@/shared/constants/event';
import { FieldType } from '@/shared/types/existing-brands';
import { SIGN_UP_LOGOS } from '@/shared/constants/existing-brands';
import { appBookDemos, appCalendarAssigns, logCommonView, sendNotificationEmail } from '@/lib/service';
import { useRouter } from 'next/router';
import { useWindowSize } from 'react-use';
import { Trustpilot } from '@/components/Home';
import Link from 'next/link';
import {
  ANNUAL_SALES_OPTIONS,
  CURRENTLY_HANDLING_FULFILLMENT_OPTIONS,
  CURRENTLY_HANDLING_SOURCING_OPTIONS,
  INTERESTED_SERVICES_KEYS,
  MONTHLY_FULFILLMENT_VOLUME_OPTIONS,
  SERVICES_OF_INTEREST_OPTIONS,
  TEAM_SIZE_OPTIONS,
} from '@/shared/constants/register';
import { showRequestError } from '@/shared/utils/utils';
import { PRINCE_CALENDLY_30_MIN_DEMO } from '@/shared/constants/calendly';
import { useConfigProvider } from '@/lib/context';
import { VALIDATE_DEBOUNCE_TIME } from '@/shared/constants';
import './index.less';

const ExistingBrandsSignup = ({ isFulfillment = false }: { isFulfillment?: boolean }) => {
  const { isMobile } = useConfigProvider();
  const [breakpoint, setBreakpoint] = useState<BreakpointsType>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const stepForm = Form.useWatch<number>('stepForm', form);
  const { width } = useWindowSize();
  const router = useRouter();
  const { email: initialEmail }: { email?: string } = router.query;
  const [calendlyModalData, setCalendlyModalData] = useState<{ name: string; url: string }>();

  const signupLogo = SIGN_UP_LOGOS[breakpoint];

  const handleScheduling = useCallback(async () => {
    const getFormValues = () => ({
      formValues: form.getFieldsValue(),
      form2Values: form2.getFieldsValue(),
    });

    const createNotificationEmail = ({
      firstName,
      lastName,
      email,
      storeUrl,
      brandName,
      phone,
      sales,
      teamSize,
      interestedServices,
      currentlySourcing,
      currentlyFulfillment,
      monthlyFulfillmentVolume,
      other,
    }) => `
    <div>Hey Sales team, </div><br />
    <div>${brandName} has booked a demo with us. Information on the brand:</div>
    <br />
    <div>First Name: ${firstName}</div>
    <div>Last Name: ${lastName}</div>
    <div>Brand Name: ${brandName}</div>
    <div>Website URL: ${storeUrl}</div>
    <div>Business email: ${email}</div>
    <div>Business phone number: ${phone}</div>
    <div>Estimated annual sales: ${sales}</div>
    <div>Team size: ${teamSize}</div>
    <div>Services interested in: ${interestedServices}</div>
    <div>Current sourcing arrangement: ${currentlySourcing}</div>
    <div>Current fulfillment arrangement: ${currentlyFulfillment}</div>
    <div>Current fulfillment monthly volume: ${monthlyFulfillmentVolume}</div>
    <div>Other information they shared: ${other}</div>
    <br />
    <br />
    <br />
    <div>Please check for an email notification from Calendly for the specific date and time of the demo. All of the above is also logged in Salesforce. </div>
    <br />
    <div>Good luck! </div>
  `;

    const { formValues, form2Values } = getFormValues();
    const notificationEmail = createNotificationEmail({ ...formValues, ...form2Values });

    try {
      const res = await appBookDemos({
        ...formValues,
        ...form2Values,
        interestedServices: [form2Values.interestedServices],
        websiteUrl: formValues.storeUrl,
        identifyYourself: form2Values.sales,
      });

      await appCalendarAssigns({
        ...PRINCE_CALENDLY_30_MIN_DEMO,
        bookDemoId: res.id,
      });

      await sendNotificationEmail(
        'creators@pietrastudio.com',
        ['sales@pietrastudio.com'],
        ['notifications@pietrastudio.com'],
        `[Book a Demo] | ${formValues.brandName} has booked a demo with us.`,
        notificationEmail,
      );
    } catch (e) {
      showRequestError(e);
    }
  }, [form, form2]);

  const handleSubmit = useCallback(() => {
    const { email } = form.getFieldsValue();

    setCalendlyModalData(PRINCE_CALENDLY_30_MIN_DEMO);
    setIsModalOpen(true);
    handleScheduling();
    logCommonView(EventSource.EXISTING_BRANDS_SIGNUP_FORM, email);
  }, [form, handleScheduling]);

  useEffect(() => {
    if (width >= 1280) {
      setBreakpoint('1280');
    } else if (width >= 1024) {
      setBreakpoint('1024');
    } else if (width >= 768) {
      setBreakpoint('768');
    } else if (width >= 640) {
      setBreakpoint('640');
    } else {
      setBreakpoint('300');
    }
  }, [width]);

  useEffect(() => {
    form.setFieldValue('stepForm', 1);
    if (initialEmail) {
      form.setFieldsValue({ email: decodeURI(initialEmail) });
    }
  }, [initialEmail, form]);

  return (
    <>
      <div className="existing-brands-signup">
        <div className="signup-container">
          <div className="form-top-container">
            <div className="form-header">
              <Link href={'/'} className="logo">
                <Image
                  width={80}
                  height={21}
                  src="/images/header/small-logo.png"
                  alt="Pietra"
                  title="Pietra"
                  loading="eager"
                />
              </Link>
              <div className="subtitle ">
                {stepForm === 3 ? (
                  <div>
                    <div>Thank you for your interest in Pietra.</div>
                    <div>Your interest has been recorded and a member of our team will reach out.</div>
                    <div>In the meantime, feel free to create an account and browse the Pietra platform!</div>
                  </div>
                ) : isFulfillment ? (
                  "Let's get you connected with a Pietra fulfillment specialist"
                ) : (
                  "Let's get you connected with a Pietra specialist"
                )}
              </div>
            </div>
            <Form.Provider
              onFormFinish={(name) => {
                if (name === 'step1') {
                  logCommonView(EventSource.EXISTING_BRANDS_SIGNUP_FORM, 'BOOK A DEMO NEXT');
                  form.setFieldValue('stepForm', 2);
                }
                if (name === 'step2') {
                  handleSubmit();
                }
              }}
            >
              <Form
                className="signup-form"
                form={form}
                size={'large'}
                layout={'vertical'}
                requiredMark={false}
                name="step1"
                hidden={stepForm !== 1}
              >
                <Form.Item name="stepForm" hidden>
                  <Input />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item<FieldType>
                      name="firstName"
                      label="First name"
                      rules={[{ required: true, message: 'Please enter first name' }]}
                      className="email-field"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item<FieldType>
                      name="lastName"
                      label="Last name"
                      rules={[{ required: true, message: 'Please enter last name' }]}
                      className="email-field"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item<FieldType>
                  name="brandName"
                  label="Brand name"
                  rules={[{ required: true, message: 'Please enter your brand name' }]}
                  className="email-field"
                >
                  <Input />
                </Form.Item>
                <Form.Item<FieldType>
                  name="storeUrl"
                  label="Website URL"
                  rules={[
                    { required: true, message: "Please enter your brand's website URL" },
                    { type: 'url', message: 'Please enter a valid url starting with www' },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item<FieldType>
                  name="email"
                  label="Business email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { required: false, type: 'email', message: 'Please enter valid email' },
                  ]}
                  validateDebounce={VALIDATE_DEBOUNCE_TIME}
                  className="email-field"
                >
                  <Input />
                </Form.Item>
                <Form.Item<FieldType>
                  name="phone"
                  label="Business phone number"
                  rules={[{ required: true, message: 'Please enter your business phone number' }]}
                  className="email-field"
                  validateDebounce={VALIDATE_DEBOUNCE_TIME}
                >
                  <Input />
                </Form.Item>
                <Form.Item className="submit-button">
                  <Button type="primary" htmlType="submit" className="btn" block>
                    <p>Next</p>
                  </Button>
                </Form.Item>
              </Form>
              <Form
                className="signup-form"
                form={form2}
                size={'large'}
                layout={'vertical'}
                requiredMark={false}
                name="step2"
                hidden={stepForm !== 2}
              >
                <Form.Item<FieldType>
                  name="sales"
                  label="What's your brand's annual sales?"
                  rules={[{ required: true, message: "Please select what's your brand's annual sales" }]}
                  className="email-field"
                >
                  <Select allowClear placeholder="E.g. $10K - $100K" options={ANNUAL_SALES_OPTIONS} />
                </Form.Item>
                <Form.Item<FieldType>
                  name="teamSize"
                  label="How large is your team?"
                  rules={[{ required: true, message: 'Please select how large is your team' }]}
                  className="email-field"
                >
                  <Select allowClear placeholder="E.g. 2-5" options={TEAM_SIZE_OPTIONS} />
                </Form.Item>
                <Form.Item<FieldType>
                  name="interestedServices"
                  label="What services are you most interested in?"
                  rules={[{ required: true, message: "Please select which services you're most interested in." }]}
                  className="email-field"
                >
                  <Select
                    getPopupContainer={() => document.getElementById('step2')}
                    popupClassName={'services-filed-select-dropdown'}
                    allowClear
                    placeholder="E.g. Sourcing & Production"
                    options={SERVICES_OF_INTEREST_OPTIONS}
                  />
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, nextValues) =>
                    prevValues.interestedServices !== nextValues.interestedServices
                  }
                >
                  {({ getFieldValue }) => {
                    const interested = getFieldValue('interestedServices');
                    return interested?.includes(INTERESTED_SERVICES_KEYS.SOURCING_PRODUCTION) ? (
                      <Form.Item<FieldType>
                        name="currentlySourcing"
                        label="How are you currently handling sourcing?"
                        rules={[{ required: true, message: 'Please select how are you currently handling sourcing' }]}
                      >
                        <Select
                          allowClear
                          placeholder="E.g. In-house department"
                          options={CURRENTLY_HANDLING_SOURCING_OPTIONS}
                        />
                      </Form.Item>
                    ) : null;
                  }}
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, nextValues) =>
                    prevValues.interestedServices !== nextValues.interestedServices
                  }
                >
                  {({ getFieldValue }) => {
                    const interested = getFieldValue('interestedServices');
                    return interested?.includes(INTERESTED_SERVICES_KEYS.LOGISTICS_FULFILLMENT) ? (
                      <>
                        <Form.Item<FieldType>
                          name="currentlyFulfillment"
                          label="How are you currently handling fulfillment?"
                          rules={[
                            { required: true, message: 'Please select how are you currently handling fulfillment' },
                          ]}
                        >
                          <Select
                            allowClear
                            placeholder="E.g. ShipBob"
                            options={CURRENTLY_HANDLING_FULFILLMENT_OPTIONS}
                          />
                        </Form.Item>
                        <Form.Item<FieldType>
                          name="monthlyFulfillmentVolume"
                          label="What's your current monthly fulfillment volume?"
                          rules={[
                            { required: true, message: "Please select what's your current monthly fulfillment volume" },
                          ]}
                        >
                          <Select allowClear placeholder="E.g. 101-500" options={MONTHLY_FULFILLMENT_VOLUME_OPTIONS} />
                        </Form.Item>
                      </>
                    ) : null;
                  }}
                </Form.Item>
                <Form.Item<FieldType>
                  name="other"
                  label="Anything else you'd like to share before we meet (optional)"
                  rules={[
                    { required: false, message: "Please input anything else you'd like to share before we meet" },
                  ]}
                >
                  <Input.TextArea
                    autoSize={{ minRows: 4 }}
                    allowClear
                    placeholder="E.g. How does Pietra's Sourcing, Fulfillment, and Marketing services integrate with each other? "
                  />
                </Form.Item>
                <Form.Item className="submit-button">
                  <Button type="primary" htmlType="submit" className="btn" block>
                    <p>Book a demo</p>
                  </Button>
                </Form.Item>
              </Form>
            </Form.Provider>
          </div>
          <div className="logo">
            {!isFulfillment && <Trustpilot className="sign-up-page-rating-wrap" />}
            {isFulfillment && (
              <div className="value-block">
                <div className="value">
                  <div className="value-add">$1.50/order</div>
                  <span className="descriptor">$3 with competitors</span>
                </div>
                <div className="value">
                  <div className="value-add">-20% shipping</div>
                  <span className="descriptor">vs. competitors</span>
                </div>
                <div className="value">
                  <div className="value-add">No fees</div>
                  <span className="descriptor">vs. competitors</span>
                </div>
              </div>
            )}
            <Image
              src={signupLogo?.link}
              alt={signupLogo?.title}
              width={signupLogo?.width}
              height={signupLogo?.height}
              style={breakpoint === '300' ? { width: '100%', height: '100%', objectFit: 'contain' } : {}}
            />
            {isFulfillment && <Trustpilot className="sign-up-page-rating-wrap" />}
          </div>
        </div>
      </div>
      <Modal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setCalendlyModalData(null);
          form.setFieldValue('stepForm', 3);
        }}
        maskClosable={false}
        width={1000}
        rootClassName={'calendly-modal-warp'}
        footer={null}
        centered
      >
        <Image
          width={120}
          height={28}
          src="/images/header/small-logo.png"
          alt="Pietra"
          title="Pietra"
          loading="eager"
        />
        <iframe
          frameBorder={0}
          src={calendlyModalData?.url}
          title={`Pietra-${calendlyModalData?.name}-Calendly`}
          style={
            isMobile
              ? { width: '100%', height: '84vh' }
              : {
                  width: '1000px',
                  height: '680px',
                }
          }
        ></iframe>
      </Modal>
    </>
  );
};

export default ExistingBrandsSignup;
